import { type ElementType, FC, useMemo } from "react";
import type { TextFieldProps } from "@mui/material/TextField";
import { useForkRef } from "@mui/material/utils";
import type { GridFilterInputValueProps } from "@mui/x-data-grid";
import { useGridRootProps } from "@mui/x-data-grid-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT } from "./announcementAdminStatus";

const PLACEHOLDER = "MM/DD/YYYY hh:mm AM/PM";

/**
 * Renders the picker's TextField with merged refs (grid focus + MUI input).
 */
const DeliveryFilterPickerTextField: FC<{
	params: TextFieldProps;
	focusElementRef: GridFilterInputValueProps["focusElementRef"];
	BaseTextField: ElementType<TextFieldProps>;
	baseTextFieldProps: Partial<TextFieldProps>;
}> = ({params, focusElementRef, BaseTextField, baseTextFieldProps}) => {
	const inputRef = useForkRef(
		focusElementRef as React.Ref<HTMLInputElement>,
		params.inputRef as React.Ref<HTMLInputElement>
	);

	return (
		<BaseTextField
			{...params}
			{...baseTextFieldProps}
			className="announcements-delivery-filter-field"
			variant="standard"
			inputRef={inputRef}
			InputLabelProps={{
				...params.InputLabelProps,
				...baseTextFieldProps.InputLabelProps,
				shrink: true,
			}}
			// Keep picker's calendar adornment if `baseTextField` overrides `InputProps`.
			InputProps={{
				...params.InputProps,
				...baseTextFieldProps.InputProps,
			}}
			inputProps={{
				...params.inputProps,
				...baseTextFieldProps.inputProps,
				placeholder: PLACEHOLDER,
			}}
		/>
	);
};

/**
 * Delivery filter: MUI `DateTimePicker` (12h, MM/DD/YYYY hh:mm A) with calendar icon.
 * Stores ISO strings for {@link gridFilterModelToListParams}.
 */
export const DeliveryDateTimeFilterInput: FC<GridFilterInputValueProps> = ({
	item,
	applyValue,
	apiRef,
	focusElementRef,
}) => {
	const rootProps = useGridRootProps();
	const BaseTextField = rootProps.components.BaseTextField;
	const baseTextFieldProps = rootProps.componentsProps?.baseTextField ?? {};

	const value = useMemo((): Dayjs | null => {
		if (item.value == null || item.value === "") {
			return null;
		}
		const d = dayjs(String(item.value));
		return d.isValid() ? d : null;
	}, [item.value]);

	const handleChange = (newValue: Dayjs | null) => {
		applyValue({
			...item,
			value: newValue && newValue.isValid() ? newValue.toISOString() : undefined,
		});
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<DateTimePicker
				ampm
				inputFormat={ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT}
				label={apiRef.current.getLocaleText("filterPanelInputLabel")}
				value={value}
				onChange={handleChange}
				OpenPickerButtonProps={{
					"aria-label": "Choose date and time",
					sx: {color: "inherit"},
				}}
				renderInput={(params: TextFieldProps) => (
					<DeliveryFilterPickerTextField
						params={params}
						focusElementRef={focusElementRef}
						BaseTextField={BaseTextField}
						baseTextFieldProps={baseTextFieldProps}
					/>
				)}
			/>
		</LocalizationProvider>
	);
};
